import { BaseButton } from '@/shared/components'
import { BaseDialog } from '@/shared/components/BaseDialog/BaseDialog'
import { Upload, X, FileText, Download, Trash2 } from 'lucide-react'
import React, { useState, useRef } from 'react'
import { useReceiptManagement } from '@/features/withdraws'
import { useReceiptDetail } from '../hooks/useReceiptDetail'
import { formatDate } from '@/shared/lib/date'
import { FileViewDialog } from '@/features/withdraws/dialogs/FileViewDialog'

interface ManageReceiptsDialogProps {
  isOpen: boolean
  onClose: () => void
  withdrawId: string
}

export const ManageReceiptsDialog = ({
  isOpen,
  onClose,
  withdrawId,
}: ManageReceiptsDialogProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { attachReceipt, isAttaching, deleteReceipt, isDeleting } = useReceiptManagement()

  // Загружаем данные заявки с файлами
  const { data: withdrawData, isLoading: isLoadingWithdraw } = useReceiptDetail(withdrawId, isOpen)

  const withdrawFiles = withdrawData ?? []

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      const newFiles = Array.from(files)
      setSelectedFiles((prev) => [...prev, ...newFiles])
    }
  }

  const handleRemoveSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleDownloadFile = (file: any) => {
    try {
      const byteCharacters = atob(file.base64FileData)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/octet-stream' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.originalFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error)
    }
  }

  const handleDeleteFile = (fileId: string) => {
    deleteReceipt({ withdrawId, fileId })
  }

  const handleAttachReceipts = () => {
    if (selectedFiles.length === 0) return

    // Прикрепляем каждый файл
    selectedFiles.forEach((file) => {
      attachReceipt({ withdrawId, file })
    })

    // Очищаем выбранные файлы после прикрепления
    setSelectedFiles([])
    onClose()
  }

  const handleClose = () => {
    setSelectedFiles([])
    onClose()
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <BaseDialog isVisible={isOpen}
                onClose={handleClose}
                title="Управление чеками">
      <div className="space-y-6">
        {/* Загрузка данных */}
        {isLoadingWithdraw && (
          <div className="text-center py-4 text-text-secondary">Загрузка файлов...</div>
        )}

        {/* Текущие прикрепленные чеки */}
        {!isLoadingWithdraw && withdrawFiles.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-3">Прикрепленные чеки</h3>
            <div className="space-y-2">
              {withdrawFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-bg-secondary rounded-lg"
                >
                  <FileViewDialog file={file}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-text-secondary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate text-left">{file.originalFileName}</p>
                        <p className="text-xs text-text-secondary text-left">
                          {formatDate(file.createdAt)} • {(file.totalBytesLenght / 1024).toFixed(2)}{' '}
                                                       KB
                        </p>
                      </div>
                    </div>
                  </FileViewDialog>
                  <div className="flex gap-2 ml-2">
                    <button
                      onClick={() => handleDownloadFile(file)}
                      className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
                      title="Скачать"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteFile(file.id)}
                      disabled={isDeleting}
                      className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors text-red-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Выбор новых файлов */}
        <div>
          <h3 className="text-sm font-medium mb-3">Добавить новые чеки</h3>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            // accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
            accept=".pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
          <BaseButton
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            icon={<Upload className="w-4 h-4" />}
            iconPosition="left"
            className="w-full"
          >
            Выбрать файлы
          </BaseButton>
        </div>

        {/* Список выбранных файлов */}
        {selectedFiles.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-3">Выбрано файлов: {selectedFiles.length}</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-bg-secondary rounded-lg"
                >
                  <FileViewDialog file={file}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-text-secondary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate text-left">{file.name}</p>
                        <p className="text-xs text-text-secondary text-left">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                  </FileViewDialog>
                  <button
                    onClick={() => handleRemoveSelectedFile(index)}
                    className="ml-2 text-text-secondary hover:text-text-primary"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="flex gap-3 pt-4">
          <BaseButton variant="outlined"
                      onClick={handleClose}
                      className="flex-1">
            Отмена
          </BaseButton>
          <BaseButton
            variant="primary"
            onClick={handleAttachReceipts}
            disabled={selectedFiles.length === 0 || isAttaching}
            className="flex-1"
          >
            {isAttaching ? 'Прикрепление...' : 'Прикрепить'}
          </BaseButton>
        </div>
      </div>
    </BaseDialog>
  )
}